"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Label from "@/components/form/Label";
import { ChevronDownIcon } from "@/icons";

/**
 * Custom dropdown (no native `<select>`): portal menu, dark styling, checkmark on active option.
 */
export default function DropdownSelect({
  label,
  value,
  onChange,
  options,
  placeholder = "Select…",
  disabled,
  className = "",
  /** Trigger + menu sizing (default matches compact form rows) */
  size = "sm",
  overlayZClass = "z-[100002]",
  menuZClass = "z-[100003]",
}) {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const anchorRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);
  const triggerId = useId();
  const menuId = `${triggerId}-menu`;

  useEffect(() => setMounted(true), []);

  const updateMenuRect = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight || 0;
    const viewportPadding = 12;
    const gap = 6;
    const preferredMenuHeight = 240;
    const availableBelow = Math.max(0, viewportHeight - rect.bottom - viewportPadding - gap);
    const availableAbove = Math.max(0, rect.top - viewportPadding - gap);
    const openUpward = availableBelow < 120 && availableAbove > availableBelow;
    const maxHeight = Math.max(
      64,
      Math.min(preferredMenuHeight, openUpward ? availableAbove : availableBelow),
    );
    setMenuRect({
      top: openUpward ? null : rect.bottom + gap,
      bottom: openUpward ? viewportHeight - rect.top + gap : null,
      left: rect.left,
      width: rect.width,
      maxHeight,
      openUpward,
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    const sync = () => updateMenuRect();
    sync();
    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);
    return () => {
      window.removeEventListener("resize", sync);
      window.removeEventListener("scroll", sync, true);
    };
  }, [open, updateMenuRect]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const selectedOption = options.find((o) => o.value === value);
  const displayLabel = selectedOption?.label ?? selectedOption?.value ?? placeholder;

  const triggerHeight = size === "sm" ? "h-9 text-xs" : "h-11 text-sm";

  return (
    <div ref={anchorRef} className={className}>
      {label ? <Label>{label}</Label> : null}
      <button
        type="button"
        disabled={disabled}
        id={triggerId}
        aria-controls={menuId}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={`mt-1 flex w-full items-center justify-between gap-2 rounded-lg border border-gray-800 bg-gray-950 px-3 text-left text-white shadow-theme-xs transition-colors hover:border-gray-700 focus:border-brand-500 focus:outline-hidden focus:ring-2 focus:ring-brand-500/20 disabled:cursor-not-allowed disabled:opacity-50 ${triggerHeight}`}
        onClick={() => {
          if (disabled) return;
          if (!open) updateMenuRect();
          setOpen((o) => !o);
        }}
      >
        <span className={selectedOption ? "truncate text-white" : "truncate text-gray-500"}>
          {displayLabel}
        </span>
        <ChevronDownIcon
          className={`size-4 shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && !disabled && mounted &&
        createPortal(
          <>
            <button
              type="button"
              tabIndex={-1}
              className={`fixed inset-0 cursor-default ${overlayZClass}`}
              aria-hidden
              onClick={() => setOpen(false)}
            />
            <ul
              id={menuId}
              role="listbox"
              className={`fixed overflow-auto rounded-lg border border-gray-800 bg-gray-950 py-1.5 shadow-xl ${menuZClass}`}
              style={{
                top: menuRect?.openUpward ? "auto" : (menuRect?.top ?? 0),
                bottom: menuRect?.openUpward ? (menuRect?.bottom ?? 0) : "auto",
                left: menuRect?.left ?? 0,
                width: menuRect?.width ?? 320,
                maxHeight: menuRect?.maxHeight ?? 240,
              }}
            >
              {options.map((o) => {
                const isSelected = o.value === value;
                return (
                  <li key={String(o.value)} role="option" aria-selected={isSelected}>
                    <button
                      type="button"
                      className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-gray-200 hover:bg-white/5 ${
                        isSelected ? "bg-white/5" : ""
                      }`}
                      onClick={() => {
                        onChange(o.value);
                        setOpen(false);
                      }}
                    >
                      <span className="flex w-5 shrink-0 justify-center" aria-hidden>
                        {isSelected ? (
                          <svg
                            className="size-4 text-white"
                            fill="none"
                            viewBox="0 0 14 14"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M11.6666 3.5L5.24992 9.91667L2.33325 7"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <span className="size-4" />
                        )}
                      </span>
                      <span className="truncate">{o.label ?? o.value}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </>,
          document.body,
        )}
    </div>
  );
}
